import { useTranslation } from 'react-i18next'
import {
  Box,
  Columns,
  Column,
  Text,
  IconEdit,
  Stack,
  Inline,
  IconErrorOutline,
  Badge
} from '@machobear/design-system'
import { useMutation } from '@tanstack/react-query'
import { isBefore, parse, endOfMonth, endOfDay } from 'date-fns'
import { add, remove } from '@/models/plans/apis'

import capitalize from '@/utils/capitalize'
import { showEditPaymentMethod } from '@/pages/Application/ApplicationTrustScore/blockpay/edit-payment-method-modal'
import iconAmericanExpress from '@/features/Payments/forms/credit-card-input/american-express.svg'
import iconMasterCard from '@/features/Payments/forms/credit-card-input/mastercard.svg'
import iconUnionPay from '@/features/Payments/forms/credit-card-input/unionpay.svg'
import iconVisa from '@/features/Payments/forms/credit-card-input/visa.svg'
import iconPlaceholder from '@/features/Payments/forms/credit-card-input/placeholder.svg'
import { useGetProduct } from '@/hooks/use-block-and-pay'

type Props = {
  card: any
  client: any
  refetch: () => void
  state: string
  couponCode: string
}

const PaymentMethod = ({ card, client, refetch, state, couponCode }: Props) => {
  const { t } = useTranslation()

  const { data } = useGetProduct('credit-check-single-package', {
    couponCode,
    state
  })

  const addMethod = useMutation({
    mutationFn: values => add(values),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['checkoutPaymentMethod'] })
    }
  })
  const deletePaymentMethod = useMutation({
    mutationFn: remove,
    onSuccess: () => {
      refetch()
      client.invalidateQueries({ queryKey: ['checkoutPaymentMethod'] })
    }
  })

  const selectCardLogo = (type: string) => {
    switch (type) {
      case 'visa':
        return iconVisa
      case 'amex':
        return iconAmericanExpress
      case 'mastercard':
        return iconMasterCard
      case 'unionpay':
        return iconUnionPay
      default:
        return iconPlaceholder
    }
  }

  const handleEdit = () => {
    showEditPaymentMethod({
      product: data?.products[0],
      card,
      mutation: addMethod,
      deletePaymentMethod: deletePaymentMethod,
      client: client,
      onSuccess: refetch
    })
  }

  const currentDate = new Date()
  const expirationDate =
    card?.expiration_date && endOfDay(endOfMonth(parse(card?.expiration_date, 'MMyy', new Date())))

  const isCreditCardExpired = isBefore(expirationDate, currentDate)

  return (
    <Stack space="xsmall">
      <Box
        display="flex"
        flexDirection="row"
        gap="small"
        justifyContent="spaceBetween"
        alignItems="flexStart"
        borderRadius={'large'}
        padding="small"
        boxShadow="borderField"
      >
        <Columns space={'small'}>
          <Column width="content">
            <Box component="img" src={selectCardLogo(card.brand)} alt={card.brand} />
          </Column>
          <Column width="content">
            <Stack space="small" align="left">
              <Inline alignY="center" space="xxsmall">
                <Text weight="strong">{`${capitalize(card.brand)} **** ${card.card_number}`}</Text>
                {isCreditCardExpired && (
                  <Badge tone="critical">
                    {t('l.standalone_trustscore.list.credit_check_modal.expired', 'Expired')}
                  </Badge>
                )}
              </Inline>

              <Stack space="xsmall" align="left">
                <Text size="small" tone="secondary">{`${card.first_name} ${card.last_name}`}</Text>

                <Text size="small" tone="secondary">
                  {`${t(
                    'l.standalone_trustscore.list.credit_check_modal.expiration',
                    'Expires'
                  )} ${card?.expiration_date?.slice(0, 2)}/${card?.expiration_date?.slice(2)}`}
                </Text>
              </Stack>
            </Stack>
          </Column>
        </Columns>
        <Inline space="none" alignY={'center'}>
          <Box role="button" onClick={handleEdit} cursor="pointer">
            <Inline space="xxsmall" align="left" alignY={'center'}>
              <IconEdit />
              <Text weight="strong">{t('common.edit', 'Edit')}</Text>
            </Inline>
          </Box>
        </Inline>
      </Box>
      {isCreditCardExpired && (
        <Text size="small" icon={<IconErrorOutline tone="critical" />} tone="critical">
          {t(
            'r.payment.edit.payment_method.expired.validation.text',
            'Please update your payment method.'
          )}
        </Text>
      )}
    </Stack>
  )
}

export default PaymentMethod
